import agwaFilterRailStyles from './agwa-filter-rail.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import type { SlCheckbox, SlInput } from '@shoelace-style/shoelace';
import type { FilterFacet, FilterSelection } from './filter-facets';
import { selectedValues } from './filter-facets';

/**
 * `<agwa-filter-rail>` - a vertical rail of faceted filters down the side of a list.
 *
 * Replaces the toolbar of dropdowns a list page would otherwise carry, and the reason is not
 * taste. A `sl-select multiple` hides every option until it is opened, and then grows TALLER as
 * values are chosen because each choice adds a wrapping tag chip - so choosing a second value
 * pushes the list down while the reader is looking at it. A rail of checkboxes shows every option
 * without opening anything and occupies the same space ticked or unticked.
 *
 * Sections are OPEN by default and collapse only on request. A rail whose options are hidden
 * behind a disclosure is the dropdown again, just wider; collapsing exists so a screen carrying a
 * dozen facets still fits on one page, not as the resting state. A collapsed section still shows
 * how many of its values are applied, so collapsing can never hide a filter that is narrowing the
 * list.
 *
 * The WHOLE rail can be hidden too (architect, 2026-09-06: "side bar can be hidden"), folding to
 * a narrow strip that gives the table the width back. The strip still carries the count of
 * applied values, and the host's query sentence stays above the table, so hiding the rail never
 * hides that the list is narrowed. Given a `storage-key`, the choice is remembered per viewer in
 * localStorage - a preference about screen space, not data, so browser storage is the right home.
 *
 * A facet declared `narrowable` - one whose values are data and grow without bound, such as
 * series / range - is capped in height, scrolls inside itself and carries a narrowing box
 * (architect, 2026-09-06: "a list control with slider rather than unlimited size growth").
 * A catalogue facet shows every value however many it has: "its almost a fixed size that wont
 * grow much so no point needing to scroll all the time when we have space" (architect, the
 * same day, on an eight-value operation-types facet that had been capped). Ticked values
 * always stay visible through the narrowing, so nothing that is filtering the list can be
 * hidden by the search for another value.
 *
 * The rail sets the selection and nothing else. It does not filter, does not know what a row is,
 * and does not own the free-text search that usually sits beside it - which is why "Clear all"
 * asks the host to clear rather than clearing itself.
 *
 * Emits:
 * - `filter-change` - `{ selection }`, a new object, whenever a box is ticked or unticked.
 * - `filter-clear` - the person asked for everything to be cleared, search included.
 * - `rail-toggle` - `{ hidden }`, after the rail was hidden or shown.
 */
@customElement('agwa-filter-rail')
export class AgwaFilterRail extends LitElement {
  static styles = unsafeCSS(agwaFilterRailStyles);

  @property({ type: Array }) facets: FilterFacet[] = [];

  @property({ type: Object }) selection: FilterSelection = {};

  /**
   * Whether anything is currently narrowing the list, INCLUDING state the rail cannot see such as
   * the host's search box. The host owns the answer; the rail only renders it, because a "Clear
   * all" that is enabled while there is nothing to clear claims an effect it does not have.
   */
  @property({ type: Boolean, attribute: 'can-clear' }) canClear = false;

  /** The rail's own heading. Not a domain word, so it is not a term. */
  @property({ type: String }) heading = 'Filters';

  /**
   * Identifies this rail's screen for the remembered hide / show choice. Empty means the choice
   * is not remembered and the rail opens on every visit.
   */
  @property({ type: String, attribute: 'storage-key' }) storageKey = '';

  /**
   * Whether the rail is folded to its strip. Reflected so the stylesheet can size the host from
   * it. Named `railHidden` rather than `hidden` because the global `hidden` attribute would remove
   * the strip too, and the strip is the way back.
   */
  @property({ type: Boolean, reflect: true, attribute: 'rail-hidden' })
  railHidden = false;

  /** Facet keys the person has collapsed. Open is the default, so this holds the exceptions. */
  @state() private collapsed = new Set<string>();

  /** Narrowing text per facet key, for facets large enough to carry a narrowing box. */
  @state() private narrowing = new Map<string, string>();

  connectedCallback() {
    super.connectedCallback();
    this.railHidden = this.readRememberedHidden();
  }

  /** The localStorage key for this screen's choice, or null when nothing is remembered. */
  private get rememberKey(): string | null {
    return this.storageKey
      ? `agwa.filter-rail.${this.storageKey}.hidden`
      : null;
  }

  /**
   * localStorage can be absent or throw (private windows, blocked site data), and a rail that
   * failed to open because a preference could not be read would be a worse failure than
   * forgetting the preference - so every access is guarded and the default is "shown".
   */
  private readRememberedHidden(): boolean {
    const key = this.rememberKey;
    if (!key) return false;
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  private rememberHidden(hidden: boolean) {
    const key = this.rememberKey;
    if (!key) return;
    try {
      localStorage.setItem(key, hidden ? '1' : '0');
    } catch {
      // Not remembered this time; the rail still hides for this visit.
    }
  }

  private toggleRail() {
    this.railHidden = !this.railHidden;
    this.rememberHidden(this.railHidden);
    this.dispatchEvent(
      new CustomEvent('rail-toggle', {
        detail: { hidden: this.railHidden },
        bubbles: true,
        composed: true,
      })
    );
    // The button just used is replaced by its counterpart (strip <-> rail head), which
    // would drop focus to the document; hand it to the counterpart so a keyboard user keeps
    // their place and can toggle straight back.
    void this.updateComplete.then(() => {
      this.renderRoot
        .querySelector<HTMLElement>(this.railHidden ? '.strip' : '.hide')
        ?.focus();
    });
  }

  private toggleSection(key: string) {
    const next = new Set(this.collapsed);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.collapsed = next;
  }

  private setNarrowing(key: string, text: string) {
    const next = new Map(this.narrowing);
    if (text) next.set(key, text);
    else next.delete(key);
    this.narrowing = next;
  }

  /** How many values are applied across every facet - what the folded strip reports. */
  private get totalApplied(): number {
    return this.facets.reduce(
      (sum, facet) => sum + selectedValues(this.selection, facet.key).length,
      0
    );
  }

  private handleOptionChange(
    facetKey: string,
    value: string,
    checked: boolean
  ) {
    const current = selectedValues(this.selection, facetKey);
    const next = checked
      ? [...current, value]
      : current.filter((v) => v !== value);
    // A new object rather than a mutation: Lit compares by reference, so mutating in place would
    // leave any other consumer of this selection rendering the previous query.
    this.dispatchEvent(
      new CustomEvent('filter-change', {
        detail: { selection: { ...this.selection, [facetKey]: next } },
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderOption(facet: FilterFacet, index: number) {
    const option = facet.options[index];
    const chosen = selectedValues(this.selection, facet.key).includes(
      option.value
    );
    return html`
      <label class="opt">
        <sl-checkbox
          size="small"
          ?checked=${chosen}
          data-facet=${facet.key}
          data-value=${option.value}
          @sl-change=${(e: Event) =>
            this.handleOptionChange(
              facet.key,
              option.value,
              (e.target as SlCheckbox).checked
            )}
          >${option.label}</sl-checkbox
        >
        ${
          option.count === undefined
            ? null
            : html`<span class="count" aria-hidden="true"
                >${option.count}</span
              >`
        }
      </label>
    `;
  }

  /**
   * The option indexes to render: all of them, or those whose label contains the narrowing text.
   * A ticked value is never narrowed away - it is filtering the list, and a rail that hid it
   * would be showing fewer ticks than the query actually holds.
   */
  private visibleOptions(facet: FilterFacet): number[] {
    const text = (this.narrowing.get(facet.key) ?? '').trim().toLowerCase();
    const chosen = selectedValues(this.selection, facet.key);
    return facet.options
      .map((option, index) => ({ option, index }))
      .filter(
        ({ option }) =>
          !text ||
          chosen.includes(option.value) ||
          option.label.toLowerCase().includes(text)
      )
      .map(({ index }) => index);
  }

  private renderNarrowing(facet: FilterFacet) {
    // Only a facet declared unbounded narrows; an empty one has nothing to narrow.
    if (!facet.narrowable || facet.options.length === 0) return null;
    return html`
      <!-- type="search": a search box is not a credential field, which is what keeps Safari and password managers from offering saved logins here (FORM-AUTOFILL; data-entry-autofill.md). -->
      <sl-input
        type="search"
        class="narrow"
        size="small"
        clearable
        label="Narrow ${facet.title.toLowerCase()}"
        placeholder="Narrow ${facet.title.toLowerCase()}"
        .value=${this.narrowing.get(facet.key) ?? ''}
        @sl-input=${(e: Event) =>
          this.setNarrowing(facet.key, (e.target as SlInput).value)}
        @sl-clear=${() => this.setNarrowing(facet.key, '')}
      >
        <sl-icon name="search" slot="prefix"></sl-icon>
      </sl-input>
    `;
  }

  private renderFacet(facet: FilterFacet) {
    const open = !this.collapsed.has(facet.key);
    const applied = selectedValues(this.selection, facet.key).length;
    const panelId = `facet-${facet.key}`;
    const visible = this.visibleOptions(facet);
    return html`
      <section class="facet">
        <h2>
          <!--
            A native button rather than sl-details: this is a heading that happens to disclose, and
            sl-details brings a card, a border and an animation that would each fight the density
            this rail exists to achieve.
          -->
          <button
            type="button"
            class="facet-toggle"
            aria-expanded=${open}
            aria-controls=${panelId}
            @click=${() => this.toggleSection(facet.key)}
          >
            <sl-icon
              class="caret"
              name=${open ? 'chevron-down' : 'chevron-right'}
            ></sl-icon>
            <span class="facet-title">${facet.title}</span>
            ${
              applied > 0
                ? html`<span
                    class="applied"
                    title="${applied} applied in this group"
                    >${applied}</span
                  >`
                : null
            }
          </button>
        </h2>
        <div id=${panelId} class="panel" ?hidden=${!open}>
          ${this.renderNarrowing(facet)}
          ${
            facet.options.length === 0 && facet.emptyHint
              ? html`<p class="empty-hint">${facet.emptyHint}</p>`
              : null
          }
          <div class="opts ${facet.narrowable ? 'opts--capped' : ''}">
            ${visible.map((index) => this.renderOption(facet, index))}
            ${
              visible.length === 0 && facet.options.length > 0
                ? html`<p class="empty-hint">No values match.</p>`
                : null
            }
          </div>
        </div>
      </section>
    `;
  }

  /**
   * The folded rail: a strip wide enough for one icon, carrying the applied count so a hidden
   * rail can never hide that the list is narrowed. The whole strip is the button back.
   */
  private renderStrip() {
    const applied = this.totalApplied;
    return html`
      <button
        type="button"
        class="strip"
        title="Show ${this.heading.toLowerCase()}"
        aria-label="Show ${this.heading.toLowerCase()}${
          applied > 0 ? `, ${applied} applied` : ''
        }"
        aria-expanded="false"
        @click=${() => this.toggleRail()}
      >
        <sl-icon name="funnel"></sl-icon>
        ${
          applied > 0
            ? html`<span class="applied" title="${applied} applied"
                >${applied}</span
              >`
            : null
        }
      </button>
    `;
  }

  render() {
    if (this.railHidden) return this.renderStrip();
    return html`
      <div class="rail-head">
        <span class="rail-title">${this.heading}</span>
        <span class="rail-actions">
          <sl-button
            variant="text"
            size="small"
            ?disabled=${!this.canClear}
            @click=${() =>
              this.dispatchEvent(
                new CustomEvent('filter-clear', {
                  bubbles: true,
                  composed: true,
                })
              )}
            >Clear all</sl-button
          >
          <button
            type="button"
            class="hide"
            title="Hide ${this.heading.toLowerCase()}"
            aria-label="Hide ${this.heading.toLowerCase()}"
            aria-expanded="true"
            @click=${() => this.toggleRail()}
          >
            <sl-icon name="chevron-double-left"></sl-icon>
          </button>
        </span>
      </div>
      ${this.facets.map((facet) => this.renderFacet(facet))}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-filter-rail': AgwaFilterRail;
  }
}
